import PropTypes from 'prop-types';

/**
 * Inline SVG icon components for the Dashboard rule builder.
 * Glyphs match Figma design node 4097:36374 exactly.
 * Path data sourced from Figma asset exports; colors are hardcoded to Figma design tokens.
 */

/** Plus icon used in the "Add Rule" header button — fill: #113E57 */
export const DashboardIconPlus = ({ className }) => (
  <svg
    viewBox="0 0 12.8 12.8"
    width="1em"
    height="1em"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M10.8777 4.4777H8.34532V1.93381C8.34532 0.863309 7.48201 0 6.41151 0C5.34101 0 4.4777 0.863309 4.4777 1.93381V4.46619H1.93381C0.863309 4.46619 0 5.3295 0 6.4C0 7.4705 0.863309 8.33381 1.93381 8.33381H4.46619V10.8662C4.46619 11.9367 5.3295 12.8 6.4 12.8C7.4705 12.8 8.33381 11.9367 8.33381 10.8662V8.33381H10.8662C11.9367 8.33381 12.8 7.4705 12.8 6.4C12.8 5.3295 11.9367 4.46619 10.8662 4.46619L10.8777 4.4777Z"
      fill="#113E57"
    />
  </svg>
);

DashboardIconPlus.propTypes = { className: PropTypes.string };

/** Rounded plus icon used in the "Add condition" button — fill: #003252 */
export const DashboardIconAdd = ({ className, fill = '#003252' }) => (
  <svg
    viewBox="0 0 14 14"
    width="1em"
    height="1em"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M4.88579 2.11421L4.88579 4.88579H2.11421C0.941624 4.88579 0 5.82741 0 7C0 8.17259 0.941624 9.11421 2.11421 9.11421H4.88579L4.88579 11.8858C4.88579 13.0584 5.82741 14 7 14C8.17259 14 9.11421 13.0584 9.11421 11.8858V9.11421H11.8858C13.0584 9.11421 14 8.17259 14 7C14 5.82741 13.0584 4.88579 11.8858 4.88579H9.11421V2.11421C9.11421 0.941624 8.17259 0 7 0C5.82741 0 4.88579 0.941624 4.88579 2.11421Z"
      fill={fill}
    />
  </svg>
);

DashboardIconAdd.propTypes = { className: PropTypes.string, fill: PropTypes.string };

/** Small trend-up icon used before the "Recent Activity" section title. */
export const DashboardRecentActivityIcon = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M18.3332 5.83203L11.2498 12.9154L7.08317 8.7487L1.6665 14.1654"
      stroke="#003252"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3335 5.83203H18.3335V10.832"
      stroke="#003252"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

DashboardRecentActivityIcon.propTypes = { className: PropTypes.string };

/**
 * Chevron/caret for the expand-collapse toggle.
 * This glyph naturally points UP ("Karat up" in Figma).
 * CSS rotates it 180deg by default (collapsed = down); resets to 0deg when expanded.
 * fill: #003252
 */
export const DashboardIconCaret = ({ className }) => (
  <svg
    viewBox="0 0 14.4 9.42927"
    width="1em"
    height="1em"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M2.58845 9.03958L7.1978 4.43023L11.8071 9.03958C12.4162 9.565 13.3237 9.565 13.9088 9.00375C14.5536 8.39475 14.5656 7.36779 13.9327 6.74685L7.1978 0L0.462895 6.74685C-0.169995 7.37973 -0.146112 8.39475 0.486778 9.00375C1.0719 9.565 1.97944 9.565 2.58845 9.03958Z"
      fill="#003252"
    />
  </svg>
);

DashboardIconCaret.propTypes = { className: PropTypes.string };

/** Trash/delete icon for delete-condition and delete-rule actions — fill: #ff5f5f */
export const DashboardIconTrash = ({ className, fill = '#ff5f5f' }) => (
  <svg
    viewBox="0 0 16.6796 17.6"
    width="1em"
    height="1em"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path d="M6.47343 6.49219C5.9621 6.49219 5.55304 6.90125 5.55304 7.41258V12.9759C5.55304 13.4872 5.9621 13.8963 6.47343 13.8963C6.98476 13.8963 7.39383 13.4872 7.39383 12.9759V7.41258C7.39383 6.90125 6.98476 6.49219 6.47343 6.49219Z" />
    <path d="M10.1859 6.49219C9.6746 6.49219 9.26554 6.90125 9.26554 7.41258V12.9759C9.26554 13.4872 9.6746 13.8963 10.1859 13.8963C10.6973 13.8963 11.1063 13.4872 11.1063 12.9759V7.41258C11.1063 6.90125 10.6973 6.49219 10.1859 6.49219Z" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.0367 2.78164H15.7592C16.2705 2.78164 16.6796 3.1907 16.6796 3.70203C16.6796 4.21336 16.2705 4.62243 15.7592 4.62243H14.8286V14.8286C14.8286 16.3524 13.5912 17.6 12.0572 17.6H4.63266C3.10889 17.6 1.86124 16.3626 1.86124 14.8286V4.6122H0.920395C0.409064 4.6122 0 4.20314 0 3.69181C0 3.18048 0.409064 2.77141 0.920395 2.77141H4.64288C4.64288 1.24765 5.8803 0 7.41429 0H9.26531C10.7891 0 12.0367 1.24765 12.0367 2.78164ZM7.41429 1.83056C6.89274 1.83056 6.47345 2.26008 6.47345 2.77141H10.2062C10.2062 2.24986 9.77664 1.83056 9.26531 1.83056H7.41429ZM12.0367 15.7694C12.5583 15.7694 12.9776 15.3501 12.9776 14.8388V4.62243H3.68158V14.8388C3.68158 15.3501 4.10087 15.7694 4.6122 15.7694H12.0367Z"
    />
  </svg>
);

DashboardIconTrash.propTypes = { className: PropTypes.string, fill: PropTypes.string };

/**
 * Minus bar for the number-stepper decrement button — fill: #1C1C1C.
 * The viewBox is padded vertically (0 -4.375 10 10) so the 10×1.25 bar
 * appears centred within the 1em×1em rendered square.
 */
export const DashboardIconStepperMinus = ({ className }) => (
  <svg
    viewBox="0 -4.375 10 10"
    width="1em"
    height="1em"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M9.79167 0H0.208333C0.0932292 0 0 0.0932292 0 0.208333V1.04167C0 1.15677 0.0932292 1.25 0.208333 1.25H9.79167C9.90677 1.25 10 1.15677 10 1.04167V0.208333C10 0.0932292 9.90677 0 9.79167 0Z"
      fill="#1C1C1C"
    />
  </svg>
);

DashboardIconStepperMinus.propTypes = { className: PropTypes.string };

/** Cross/plus for the number-stepper increment button — fill: #1C1C1C */
export const DashboardIconStepperPlus = ({ className }) => (
  <svg
    viewBox="0 0 10 10"
    width="1em"
    height="1em"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M9.79167 4.375H5.625V0.208333C5.625 0.0932292 5.53177 0 5.41667 0H4.58333C4.46823 0 4.375 0.0932292 4.375 0.208333V4.375H0.208333C0.0932292 4.375 0 4.46823 0 4.58333V5.41667C0 5.53177 0.0932292 5.625 0.208333 5.625H4.375V9.79167C4.375 9.90677 4.46823 10 4.58333 10H5.41667C5.53177 10 5.625 9.90677 5.625 9.79167V5.625H9.79167C9.90677 5.625 10 5.53177 10 5.41667V4.58333C10 4.46823 9.90677 4.375 9.79167 4.375Z"
      fill="#1C1C1C"
    />
  </svg>
);

DashboardIconStepperPlus.propTypes = { className: PropTypes.string };

/** Info circle for field-help messages — fill: #3FA9F5 */
export const DashboardIconInfo = ({ className }) => (
  <svg
    viewBox="0 0 16.6668 16.6667"
    width="1em"
    height="1em"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M8.3335 0C3.74024 0 0.00016278 3.74008 0.00016278 8.33333C0.00016278 12.9266 3.74024 16.6667 8.3335 16.6667C12.9268 16.6667 16.6668 12.9266 16.6668 8.33333C16.6668 3.74008 12.9268 0 8.3335 0ZM8.3335 14.9504C4.6827 14.9504 1.71643 11.9841 1.71643 8.33333C1.71643 4.68254 4.67278 1.71627 8.3335 1.71627C11.9942 1.71627 14.9506 4.68254 14.9506 8.33333C14.9506 11.9841 11.9942 14.9504 8.3335 14.9504Z"
      fill="#3FA9F5"
    />
    <path
      d="M8.43275 6.16084C7.99624 6.16084 7.64902 6.51798 7.64902 6.94457V12.0537C7.64902 12.4902 8.00616 12.8374 8.43275 12.8374C8.85934 12.8374 9.21648 12.4803 9.21648 12.0537V6.94457C9.21648 6.50806 8.85934 6.16084 8.43275 6.16084Z"
      fill="#3FA9F5"
    />
    <path
      d="M9.0181 3.99786C8.83953 3.83913 8.61136 3.75977 8.38318 3.75977C8.15501 3.75977 7.92683 3.83913 7.75818 3.99786C7.65898 4.07723 7.58953 4.18635 7.54985 4.29548C7.50025 4.40461 7.49033 4.53358 7.49033 4.65262C7.49033 4.77167 7.51017 4.89072 7.54985 4.99985C7.59945 5.10897 7.65898 5.20818 7.73834 5.28754C7.82763 5.36691 7.91691 5.43635 8.02604 5.48596C8.13517 5.52564 8.25421 5.5554 8.37326 5.5554C8.49231 5.5554 8.61136 5.53556 8.72048 5.49588C8.82961 5.45619 8.92882 5.38675 9.0181 5.29746C9.10739 5.2181 9.16691 5.11889 9.21652 5.00977C9.26612 4.90064 9.28596 4.78159 9.27604 4.66254C9.27604 4.5435 9.27604 4.41453 9.21652 4.29548C9.16691 4.17643 9.08755 4.07723 9.00818 3.99786H9.0181Z"
      fill="#3FA9F5"
    />
  </svg>
);

DashboardIconInfo.propTypes = { className: PropTypes.string };

/**
 * Cancel button icon: circle outline + X glyph, combined into a single 24×24 SVG.
 * Composed from icon_cancel_outer (20×20 → translate 2,2) and
 * icon_cancel_inner (8.8×8.8 → translate 7.6,7.6). fill: #005D98
 */
export const DashboardIconCancel = ({ className, fill = '#005D98' }) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <g transform="translate(2, 2)">
      <path d="M10 0C4.4881 0 0 4.4881 0 10C0 15.5119 4.4881 20 10 20C15.5119 20 20 15.5119 20 10C20 4.4881 15.5119 0 10 0ZM10 17.9405C5.61905 17.9405 2.05952 14.381 2.05952 10C2.05952 5.61905 5.60714 2.05952 10 2.05952C14.3929 2.05952 17.9405 5.61905 17.9405 10C17.9405 14.381 14.3929 17.9405 10 17.9405Z" />
    </g>
    <g transform="translate(7.6, 7.6)">
      <path d="M5.94769 0.491094L4.4 2.03878L2.85231 0.491094C2.19752 -0.163698 1.14589 -0.163698 0.491094 0.491094C-0.163698 1.14589 -0.163698 2.19752 0.491094 2.85231L2.03878 4.4L0.491094 5.94769C-0.163698 6.60248 -0.163698 7.65412 0.491094 8.30891C1.14589 8.9637 2.19752 8.9637 2.85231 8.30891L4.4 6.76122L5.94769 8.30891C6.60248 8.9637 7.65412 8.9637 8.30891 8.30891C8.9637 7.65412 8.9637 6.60248 8.30891 5.94769L6.76122 4.4L8.30891 2.85231C8.9637 2.19752 8.9637 1.14589 8.30891 0.491094C7.65412 -0.163698 6.60248 -0.163698 5.94769 0.491094Z" />
    </g>
  </svg>
);

DashboardIconCancel.propTypes = { className: PropTypes.string, fill: PropTypes.string };

/**
 * Save button icon: circle outline + checkmark glyph, combined into a single 24×24 SVG.
 * Composed from icon_save_outer (20×20 → translate 2,2) and
 * icon_save_inner (12.8×10.36 → translate 5.6,7.2). fill/stroke: #003252
 */
export const DashboardIconSave = ({ className, fill = '#003252' }) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <g transform="translate(2, 2)">
      <path
        d="M10 0C4.4881 0 0 4.4881 0 10C0 15.5119 4.4881 20 10 20C15.5119 20 20 15.5119 20 10C20 4.4881 15.5119 0 10 0ZM10 17.9405C5.61905 17.9405 2.05952 14.381 2.05952 10C2.05952 5.61905 5.60714 2.05952 10 2.05952C14.3929 2.05952 17.9405 5.61905 17.9405 10C17.9405 14.381 14.3929 17.9405 10 17.9405Z"
        fill={fill}
      />
    </g>
    <g transform="translate(5.6, 7.2)">
      <path d="M2 4.8L4.93334 7.6L10.8 2" stroke={fill} strokeWidth="4" strokeLinecap="round" />
    </g>
  </svg>
);

DashboardIconSave.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const CallIcon = ({ className, fill = '#ffffff' }) => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="none"
    className={className}
  >
    <g clipPath="url(#clip0_9650_11963)">
      <path
        d="M9.84205 5.55244C10.0547 5.55244 10.3561 5.65879 10.5511 5.81832C11.3132 6.45643 11.9868 9.25702 11.7564 10.2674C11.65 10.7459 11.136 11.2068 10.6397 11.6677C10.4624 11.8272 10.3029 11.9867 10.1434 12.1462C9.82432 12.4653 9.75342 12.9439 9.98385 13.3338C11.5614 16.1167 13.8657 18.421 16.6485 19.9985C16.8081 20.0872 16.9676 20.1226 17.1271 20.1226C17.3753 20.1226 17.6412 20.0162 17.8361 19.839C17.9957 19.6795 18.1552 19.5199 18.3147 19.3427C18.7756 18.8464 19.2542 18.3146 19.715 18.226C19.8568 18.1905 20.0163 18.1905 20.1936 18.1905C21.4344 18.1905 23.6323 18.7932 24.1818 19.449C24.359 19.6617 24.4477 19.9099 24.4477 20.2112V23.3486C24.4477 23.6499 24.3059 23.9513 24.0754 24.164C23.8627 24.3589 23.5968 24.4653 23.2778 24.4653C23.2423 24.4653 23.1892 24.4653 23.1537 24.4653C18.3856 24.4653 13.9366 22.6573 10.6574 19.3781C7.37824 16.099 5.57026 11.6499 5.57026 6.77548C5.53481 6.47415 5.65889 6.17282 5.87159 5.94239C6.08429 5.71196 6.38562 5.57016 6.68695 5.57016H9.84205C9.84205 5.57016 9.85977 5.57016 9.8775 5.57016M9.84205 3H9.77115H6.6515C5.64116 3 4.66627 3.44313 3.95726 4.20532C3.30143 4.93205 2.96465 5.88922 3.0001 6.84638C3.01782 12.3412 5.09168 17.4284 8.84943 21.1684C12.5895 24.9084 17.6412 26.9823 23.0828 27C23.1537 27 23.2246 27 23.2955 27C24.2704 27 25.1389 26.6632 25.8302 26.0251C26.0607 25.8124 26.2734 25.5643 26.4506 25.2984C26.8228 24.7134 27.0178 24.0399 27.0178 23.3486V20.2112C27.0355 19.3604 26.7342 18.4742 26.1493 17.7829C24.7844 16.1699 21.5053 15.6381 20.2113 15.6381C19.8391 15.6381 19.5023 15.6736 19.1833 15.7445C18.1906 15.9572 17.4462 16.5775 16.879 17.1625C15.3014 16.0812 13.9189 14.7164 12.8553 13.1388C13.4403 12.5716 14.0607 11.8272 14.2734 10.8346C14.6279 9.15067 13.848 5.28656 12.235 3.90399C11.5968 3.35451 10.7106 3.01773 9.85977 3.01773L9.84205 3Z"
        stroke="none"
        fill={fill}
      />
    </g>
    <defs>
      <clipPath id="clip0_9650_11963">
        <rect width="24" height="24" fill={fill} transform="translate(3 3)" />
      </clipPath>
    </defs>
  </svg>
);

CallIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const CallScheduledIcon = ({ className }) => (
  <svg
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M13.5675 1.91641C13.2623 1.6281 12.8976 1.48395 12.4652 1.48395H11.685V0.780133C11.685 0.347668 11.3374 0 10.9049 0C10.4724 0 10.1248 0.347668 10.1248 0.780133V1.48395H3.90067V0.780133C3.90067 0.347668 3.553 0 3.12053 0C2.68807 0 2.3404 0.347668 2.3404 0.780133V1.48395H1.56027C1.13628 1.48395 0.763174 1.6281 0.457904 1.91641C0.152635 2.20472 0 2.55239 0 2.95942V13.3301C0 13.7371 0.152635 14.0848 0.457904 14.3731C0.763174 14.6614 1.1278 14.8056 1.56027 14.8056H12.4228C13.2962 14.8056 14 14.1018 14 13.2283V2.95942C14 2.55239 13.8474 2.20472 13.5421 1.91641H13.5675ZM12.4652 13.3301H1.57723V5.92732H12.4652M12.4652 4.44337H1.57723V2.95942H12.4652V4.44337Z"
      fill="white"
    />
    <path
      d="M3.52774 6.93555H2.73912C2.58926 6.93555 2.46777 7.05703 2.46777 7.2069V7.99551C2.46777 8.14537 2.58926 8.26686 2.73912 8.26686H3.52774C3.6776 8.26686 3.79909 8.14537 3.79909 7.99551V7.2069C3.79909 7.05703 3.6776 6.93555 3.52774 6.93555Z"
      fill="white"
    />
    <path
      d="M3.52774 8.87891H2.73912C2.58926 8.87891 2.46777 9.00039 2.46777 9.15026V9.93887C2.46777 10.0887 2.58926 10.2102 2.73912 10.2102H3.52774C3.6776 10.2102 3.79909 10.0887 3.79909 9.93887V9.15026C3.79909 9.00039 3.6776 8.87891 3.52774 8.87891Z"
      fill="white"
    />
    <path
      d="M3.52774 10.8203H2.73912C2.58926 10.8203 2.46777 10.9418 2.46777 11.0917V11.8803C2.46777 12.0301 2.58926 12.1516 2.73912 12.1516H3.52774C3.6776 12.1516 3.79909 12.0301 3.79909 11.8803V11.0917C3.79909 10.9418 3.6776 10.8203 3.52774 10.8203Z"
      fill="white"
    />
    <path
      d="M5.46133 6.93555H4.67272C4.52286 6.93555 4.40137 7.05703 4.40137 7.2069V7.99551C4.40137 8.14537 4.52286 8.26686 4.67272 8.26686H5.46133C5.61119 8.26686 5.73268 8.14537 5.73268 7.99551V7.2069C5.73268 7.05703 5.61119 6.93555 5.46133 6.93555Z"
      fill="white"
    />
    <path
      d="M5.46133 8.87891H4.67272C4.52286 8.87891 4.40137 9.00039 4.40137 9.15026V9.93887C4.40137 10.0887 4.52286 10.2102 4.67272 10.2102H5.46133C5.61119 10.2102 5.73268 10.0887 5.73268 9.93887V9.15026C5.73268 9.00039 5.61119 8.87891 5.46133 8.87891Z"
      fill="white"
    />
    <path
      d="M5.46133 10.8203H4.67272C4.52286 10.8203 4.40137 10.9418 4.40137 11.0917V11.8803C4.40137 12.0301 4.52286 12.1516 4.67272 12.1516H5.46133C5.61119 12.1516 5.73268 12.0301 5.73268 11.8803V11.0917C5.73268 10.9418 5.61119 10.8203 5.46133 10.8203Z"
      fill="white"
    />
    <path
      d="M7.38614 6.93555H6.59752C6.44766 6.93555 6.32617 7.05703 6.32617 7.2069V7.99551C6.32617 8.14537 6.44766 8.26686 6.59752 8.26686H7.38614C7.536 8.26686 7.65749 8.14537 7.65749 7.99551V7.2069C7.65749 7.05703 7.536 6.93555 7.38614 6.93555Z"
      fill="white"
    />
    <path
      d="M7.38614 8.87891H6.59752C6.44766 8.87891 6.32617 9.00039 6.32617 9.15026V9.93887C6.32617 10.0887 6.44766 10.2102 6.59752 10.2102H7.38614C7.536 10.2102 7.65749 10.0887 7.65749 9.93887V9.15026C7.65749 9.00039 7.536 8.87891 7.38614 8.87891Z"
      fill="white"
    />
    <path
      d="M7.38614 10.8203H6.59752C6.44766 10.8203 6.32617 10.9418 6.32617 11.0917V11.8803C6.32617 12.0301 6.44766 12.1516 6.59752 12.1516H7.38614C7.536 12.1516 7.65749 12.0301 7.65749 11.8803V11.0917C7.65749 10.9418 7.536 10.8203 7.38614 10.8203Z"
      fill="white"
    />
    <path
      d="M9.31094 6.93555H8.52233C8.37246 6.93555 8.25098 7.05703 8.25098 7.2069V7.99551C8.25098 8.14537 8.37246 8.26686 8.52233 8.26686H9.31094C9.4608 8.26686 9.58229 8.14537 9.58229 7.99551V7.2069C9.58229 7.05703 9.4608 6.93555 9.31094 6.93555Z"
      fill="white"
    />
    <path
      d="M9.31094 8.87891H8.52233C8.37246 8.87891 8.25098 9.00039 8.25098 9.15026V9.93887C8.25098 10.0887 8.37246 10.2102 8.52233 10.2102H9.31094C9.4608 10.2102 9.58229 10.0887 9.58229 9.93887V9.15026C9.58229 9.00039 9.4608 8.87891 9.31094 8.87891Z"
      fill="white"
    />
    <path
      d="M9.31094 10.8203H8.52233C8.37246 10.8203 8.25098 10.9418 8.25098 11.0917V11.8803C8.25098 12.0301 8.37246 12.1516 8.52233 12.1516H9.31094C9.4608 12.1516 9.58229 12.0301 9.58229 11.8803V11.0917C9.58229 10.9418 9.4608 10.8203 9.31094 10.8203Z"
      fill="white"
    />
    <path
      d="M11.2445 6.93555H10.4559C10.3061 6.93555 10.1846 7.05703 10.1846 7.2069V7.99551C10.1846 8.14537 10.3061 8.26686 10.4559 8.26686H11.2445C11.3944 8.26686 11.5159 8.14537 11.5159 7.99551V7.2069C11.5159 7.05703 11.3944 6.93555 11.2445 6.93555Z"
      fill="white"
    />
    <path
      d="M11.2445 8.87891H10.4559C10.3061 8.87891 10.1846 9.00039 10.1846 9.15026V9.93887C10.1846 10.0887 10.3061 10.2102 10.4559 10.2102H11.2445C11.3944 10.2102 11.5159 10.0887 11.5159 9.93887V9.15026C11.5159 9.00039 11.3944 8.87891 11.2445 8.87891Z"
      fill="white"
    />
    <path
      d="M11.2445 10.8203H10.4559C10.3061 10.8203 10.1846 10.9418 10.1846 11.0917V11.8803C10.1846 12.0301 10.3061 12.1516 10.4559 12.1516H11.2445C11.3944 12.1516 11.5159 12.0301 11.5159 11.8803V11.0917C11.5159 10.9418 11.3944 10.8203 11.2445 10.8203Z"
      fill="white"
    />
  </svg>
);

CallScheduledIcon.propTypes = { className: PropTypes.string };

/** Schedule-call cell icon: phone arc inside a rounded calendar border — fill: #005D98 */
export const ScheduleCallCellIcon = ({ className }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M9.63346 8.83024C9.33405 9.04528 8.91488 8.96622 8.72893 8.64366L8.72263 8.64682L8.01035 7.41035C7.84961 7.13207 7.9284 6.77156 8.19315 6.58814C9.23005 5.87662 10.4813 5.45919 11.8333 5.45919C13.1854 5.45919 14.4366 5.87662 15.4735 6.58814C15.7383 6.77156 15.817 7.13207 15.6563 7.41035L14.9188 8.69426C14.7297 9.01998 14.3074 9.09587 14.0048 8.87135C13.3871 8.40965 12.596 8.13769 11.7924 8.13769C10.9887 8.13769 10.2417 8.39384 9.63346 8.83024Z"
      fill="#005D98"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.02258 2.39844H18.9782C20.4248 2.39844 21.6004 3.57799 21.6004 5.0295V17.7674C21.6004 19.2189 20.4248 20.3984 18.9782 20.3984H5.02258C3.57597 20.3984 2.40039 19.2189 2.40039 17.7674V5.0295C2.40039 3.57799 3.57597 2.39844 5.02258 2.39844ZM20.1128 5.0295C20.1128 4.40336 19.6022 3.89106 18.9782 3.89106H5.02258C4.39855 3.89106 3.88798 4.40336 3.88798 5.0295V17.7674C3.88798 18.3935 4.39855 18.9058 5.02258 18.9058H18.9782C19.6022 18.9058 20.1128 18.3935 20.1128 17.7674V5.0295Z"
      fill="#005D98"
    />
  </svg>
);

ScheduleCallCellIcon.propTypes = { className: PropTypes.string };

export const CallCompletedIcon = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M6.33301 9.99935L8.77745 12.3327L13.6663 7.66602"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M10.0003 1.66602C5.40707 1.66602 1.66699 5.40609 1.66699 9.99935C1.66699 14.5926 5.40707 18.3327 10.0003 18.3327C14.5936 18.3327 18.3337 14.5926 18.3337 9.99935C18.3337 5.40609 14.5936 1.66602 10.0003 1.66602ZM10.0003 16.6164C6.34953 16.6164 3.38326 13.6501 3.38326 9.99935C3.38326 6.34856 6.33961 3.38229 10.0003 3.38229C13.661 3.38229 16.6174 6.34856 16.6174 9.99935C16.6174 13.6501 13.661 16.6164 10.0003 16.6164Z"
      fill="white"
    />
  </svg>
);

CallCompletedIcon.propTypes = { className: PropTypes.string };

export const CallMissedIcon = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.99617 9.16732C12.8575 9.16736 15.5914 10.3302 17.6955 12.4461C18.0781 12.8014 18.3096 13.3023 18.3336 13.8353C18.3655 14.4006 18.1577 14.95 17.7672 15.3457L16.5717 16.5575C16.2449 16.8965 15.7748 17.1144 15.3046 17.1548H15.1614C14.1013 17.1548 12.4111 16.0889 11.885 15.2651C11.5901 14.8048 11.5505 14.2794 11.5505 13.8271C11.0484 13.7302 10.5379 13.6823 10.0198 13.6823C9.50173 13.6823 8.98361 13.7302 8.47354 13.8271C8.47354 14.2794 8.42585 14.8048 8.13093 15.2651C7.60488 16.0889 5.91531 17.1627 4.84724 17.1629H4.69587C4.20971 17.1225 3.75493 16.9047 3.43611 16.5656L2.24063 15.3538C1.85007 14.9581 1.64299 14.4088 1.6669 13.8516C1.69084 13.3025 1.92971 12.7939 2.34399 12.4144C4.42433 10.3226 7.14267 9.16732 9.99617 9.16732ZM9.99617 10.5646C7.49341 10.5646 5.11799 11.5821 3.28474 13.4235C3.1333 13.5608 3.04548 13.7227 3.04548 13.9004C3.0376 14.0699 3.10166 14.239 3.21313 14.3521L4.4086 15.5638C4.52019 15.6769 4.65575 15.7494 4.79923 15.7575H4.84724C5.40533 15.7572 6.6725 14.9661 6.97533 14.4977C7.11871 14.2796 7.10276 13.8918 7.08682 13.5203C7.07887 13.3993 7.07869 13.2703 7.07869 13.1493C7.07869 12.8989 7.23821 12.689 7.46931 12.6243C8.29821 12.3901 9.16696 12.2769 10.0198 12.2769C10.8726 12.2769 11.7258 12.3982 12.5548 12.6243C12.7858 12.689 12.9454 12.907 12.9454 13.1493C12.9454 13.2087 12.9433 13.27 12.9413 13.3324C12.9393 13.3969 12.9373 13.4629 12.9372 13.5285C12.9366 13.5446 12.9355 13.5611 12.9348 13.5773C12.9192 13.9336 12.9035 14.2973 13.0406 14.5059C13.3355 14.9743 14.6034 15.7656 15.1614 15.7656H15.2012C15.3446 15.7574 15.5041 15.677 15.5837 15.5882L16.7873 14.3683C16.8987 14.2473 16.962 14.0859 16.9542 13.9085C16.9462 13.7391 16.8669 13.5773 16.7475 13.4723C14.8903 11.5825 12.5069 10.5647 9.99617 10.5646Z"
      fill="white"
    />
    <path
      d="M9.46231 3.33398C9.80464 3.334 10.0704 3.60097 10.0629 3.91829C10.0552 4.22838 9.78941 4.4733 9.46231 4.47331H7.88028L10.5162 6.97331L13.1448 4.48145C13.3793 4.25911 13.7566 4.25757 13.9927 4.47738C14.2248 4.70122 14.224 5.05901 13.9895 5.28141L10.9385 8.17448L10.9345 8.1696C10.6983 8.3895 10.3202 8.38871 10.0857 8.16634L7.03555 5.2749V6.77474C7.03552 7.08483 6.77668 7.3371 6.44962 7.3444C6.11489 7.35158 5.83279 7.09208 5.83276 6.77474V3.33398H9.46231Z"
      fill="white"
    />
  </svg>
);

CallMissedIcon.propTypes = { className: PropTypes.string };

export const CallDeclinedIcon = ({ className }) => (
  <svg
    width="15"
    height="16"
    viewBox="0 0 15 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M14.1387 8.38215C13.3807 7.48404 11.5515 7.17918 10.8347 7.17918C10.6204 7.17918 10.4391 7.19565 10.2661 7.23685C9.71407 7.35221 9.30209 7.69827 8.98074 8.02785C8.09911 7.42636 7.33283 6.66832 6.73134 5.78669C7.06092 5.46534 7.40699 5.05336 7.52234 4.50131C7.72009 3.562 7.28339 1.41147 6.38528 0.636953C6.02274 0.332088 5.52836 0.142578 5.06695 0.142578H5.03399H3.29544C2.71043 0.142578 2.16662 0.389765 1.77112 0.818223C1.40034 1.22196 1.21083 1.7493 1.23554 2.28487C1.23554 4.02342 1.63104 5.68781 2.33965 7.18742C2.73515 7.07206 3.15536 7.00614 3.58382 7.00614C3.66622 7.00614 3.74861 7.00614 3.83925 7.02262C3.07297 5.59718 2.66923 3.96574 2.66923 2.24367C2.65275 2.07888 2.71043 1.90585 2.83402 1.78225C2.95762 1.65042 3.12241 1.57626 3.2872 1.57626H5.05047C5.05047 1.57626 5.05871 1.57626 5.06695 1.57626C5.19054 1.57626 5.35533 1.63394 5.46245 1.72458C5.88267 2.08712 6.26169 3.6444 6.13809 4.21293C6.08041 4.47659 5.79203 4.74026 5.51188 4.99569C5.41301 5.08632 5.32237 5.16872 5.23174 5.25935C5.05871 5.43239 5.01751 5.70429 5.1411 5.92676C6.02274 7.4758 7.30811 8.76117 8.86539 9.64281C8.94779 9.69225 9.04666 9.71696 9.1373 9.71696C9.27737 9.71696 9.42568 9.65929 9.5328 9.56041C9.61519 9.46978 9.70583 9.37914 9.79646 9.28027C10.0519 9.00012 10.3156 8.71174 10.5792 8.65406C10.6534 8.63758 10.744 8.62934 10.8511 8.62934C11.5433 8.62934 12.7627 8.95892 13.0758 9.3297C13.1747 9.44506 13.2241 9.59337 13.2241 9.75816V11.5132C13.2241 11.678 13.15 11.8428 13.0181 11.9664C12.9028 12.0735 12.7545 12.1312 12.5732 12.1312C12.5485 12.1312 12.5238 12.1312 12.499 12.1312C10.9335 12.1312 9.43392 11.7769 8.08263 11.1095C8.09911 11.2495 8.10735 11.3814 8.10735 11.5214C8.10735 11.9005 8.05791 12.2712 7.96728 12.6173C9.35977 13.2188 10.8841 13.5401 12.4661 13.5484C12.5073 13.5484 12.5485 13.5484 12.5814 13.5484C13.1253 13.5484 13.6114 13.3589 13.9986 13.0046C14.1305 12.881 14.2458 12.7409 14.3447 12.5926C14.5507 12.2712 14.6661 11.8922 14.6661 11.505V9.74992C14.6743 9.27203 14.5013 8.77765 14.1799 8.39863L14.1387 8.38215Z"
      fill="white"
    />
    <path
      d="M7.16843 11.5061C7.16843 11.745 7.14371 11.984 7.09427 12.2065C6.76469 13.8461 5.32277 15.0903 3.58422 15.0903C1.60672 15.0903 0 13.4836 0 11.5061C0 10.2702 0.626208 9.17429 1.582 8.5316C1.9363 8.29266 2.34828 8.11963 2.77674 8.02075C3.0404 7.96307 3.30407 7.92188 3.58422 7.92188C3.89732 7.92188 4.19394 7.97131 4.48233 8.04547C4.87783 8.15258 5.25685 8.30914 5.58643 8.5316C6.27032 8.99302 6.76469 9.68514 7.00364 10.4926C7.09427 10.814 7.16843 11.1518 7.16843 11.4979V11.5061ZM5.07558 9.96529C4.82015 9.70986 4.40817 9.70986 4.15275 9.96529L3.55126 10.5668L2.94977 9.96529C2.69434 9.70986 2.28236 9.70986 2.02694 9.96529C1.77151 10.2207 1.77151 10.6327 2.02694 10.8881L2.62842 11.4896L2.02694 12.0911C1.77151 12.3465 1.77151 12.7585 2.02694 13.0139C2.28236 13.2694 2.69434 13.2694 2.94977 13.0139L3.55126 12.4124L4.15275 13.0139C4.40817 13.2694 4.82015 13.2694 5.07558 13.0139C5.33101 12.7585 5.33101 12.3465 5.07558 12.0911L4.47409 11.4896L5.07558 10.8881C5.33101 10.6327 5.33101 10.2207 5.07558 9.96529Z"
      fill="white"
    />
  </svg>
);

CallDeclinedIcon.propTypes = { className: PropTypes.string };

export const CallFailedIcon = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M10 2.5C5.86 2.5 2.5 5.86 2.5 10C2.5 14.14 5.86 17.5 10 17.5C14.14 17.5 17.5 14.14 17.5 10C17.5 5.86 14.14 2.5 10 2.5ZM4 10C4 6.685 6.685 4 10 4C11.3875 4 12.6625 4.4725 13.675 5.2675L5.2675 13.675C4.4725 12.6625 4 11.3875 4 10ZM10 16C8.6125 16 7.3375 15.5275 6.325 14.7325L14.7325 6.325C15.5275 7.3375 16 8.6125 16 10C16 13.315 13.315 16 10 16Z"
      fill="white"
    />
  </svg>
);

CallFailedIcon.propTypes = { className: PropTypes.string };

export const ContactSensorIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7 4C8.10457 4 9 4.89543 9 6V18C8.9999 19.1045 8.10451 20 7 20H6C4.89549 20 4.0001 19.1045 4 18V6C4 4.89543 4.89543 4 6 4H7ZM6 5.5C5.72386 5.5 5.5 5.72386 5.5 6V18C5.5001 18.2761 5.72392 18.5 6 18.5H7C7.27608 18.5 7.4999 18.2761 7.5 18V6C7.5 5.72386 7.27614 5.5 7 5.5H6Z"
      fill={fill}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 4C19.1046 4 20 4.89543 20 6V18C19.9999 19.1045 19.1045 20 18 20H17C15.8955 20 15.0001 19.1045 15 18V6C15 4.89543 15.8954 4 17 4H18ZM17 5.5C16.7239 5.5 16.5 5.72386 16.5 6V18C16.5001 18.2761 16.7239 18.5 17 18.5H18C18.2761 18.5 18.4999 18.2761 18.5 18V6C18.5 5.72386 18.2761 5.5 18 5.5H17Z"
      fill={fill}
    />
    <path
      d="M12.3932 9.54854C12.548 9.43994 12.7957 9.51752 12.9195 9.73472C13.6935 11.1388 13.6935 12.8531 12.9195 14.2649C12.8034 14.4821 12.5557 14.5597 12.3932 14.4511C12.2307 14.3425 12.1997 14.1098 12.3003 13.9236C12.9582 12.7212 12.9582 11.2706 12.3003 10.0683C12.1997 9.88986 12.2307 9.65715 12.3932 9.54079V9.54854ZM11.6811 10.7276C11.565 10.4794 11.3019 10.3786 11.1471 10.4949C10.9923 10.6113 10.9613 10.8362 11.0465 11.0224C11.3328 11.6352 11.3328 12.3489 11.0465 12.9617C10.9613 13.1479 10.9923 13.3728 11.1471 13.4892C11.3019 13.6055 11.565 13.5047 11.6811 13.2565C12.0526 12.4575 12.0526 11.5266 11.6811 10.7276Z"
      fill={fill}
    />
  </svg>
);

ContactSensorIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const EngagementIcon = ({ className, fill = '#005d98' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M7.48182 5.59091L4.26364 8.80909C1.24545 11.8273 1.24545 16.7182 4.26364 19.7364C7.28182 22.7545 12.1727 22.7545 15.1909 19.7364L20.6545 14.2727C21.5364 13.3909 21.5364 11.9455 20.6545 11.0545C20.5455 10.9455 20.4273 10.8455 20.3 10.7636L20.6545 10.4091C21.5364 9.52727 21.5364 8.08182 20.6545 7.19091C20.5091 7.04545 20.3364 6.91818 20.1636 6.81818C20.5273 5.98182 20.3727 4.98182 19.6909 4.3C18.9 3.50909 17.6727 3.42727 16.7818 4.04545C16.6909 3.90909 16.5909 3.78182 16.4727 3.66364C15.5909 2.78182 14.1455 2.78182 13.2545 3.66364L10.9727 5.94545C10.8909 5.81818 10.7909 5.7 10.6818 5.59091C9.8 4.70909 8.36364 4.70909 7.48182 5.59091ZM8.76364 6.88182C8.94545 6.7 9.22727 6.7 9.40909 6.88182C9.59091 7.06364 9.59091 7.34545 9.40909 7.52727L6.51818 10.4182C7.58182 11.4818 7.58182 13.2091 6.51818 14.2727L7.8 15.5545C9.11818 14.2364 9.45455 12.3091 8.81818 10.6818L14.5455 4.95455C14.7273 4.77273 15.0091 4.77273 15.1909 4.95455C15.3727 5.13636 15.3727 5.41818 15.1909 5.6L11.0091 9.78182L12.2909 11.0636L17.7545 5.6C17.9364 5.41818 18.2182 5.41818 18.4 5.6C18.5818 5.78182 18.5818 6.06364 18.4 6.24545L12.9364 11.7091L14.2182 12.9909L18.7182 8.49091C18.9 8.30909 19.1818 8.30909 19.3636 8.49091C19.5455 8.67273 19.5455 8.95455 19.3636 9.13636L14.2182 14.2818L15.5 15.5636L18.7182 12.3455C18.9 12.1636 19.1818 12.1636 19.3636 12.3455C19.5455 12.5273 19.5455 12.8091 19.3636 12.9909L13.9091 18.4545C11.6 20.7636 7.86364 20.7636 5.55455 18.4545C3.24545 16.1455 3.24545 12.4091 5.55455 10.1L8.76364 6.88182ZM22 16.5455C22 19.5545 19.5545 22 16.5455 22V20.6364C18.8 20.6364 20.6364 18.8 20.6364 16.5455H22ZM2 7.45455C2 4.44545 4.44545 2 7.45455 2V3.36364C5.2 3.36364 3.36364 5.2 3.36364 7.45455H2Z"
      fill={fill}
    />
  </svg>
);

EngagementIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const TemperatureIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M13.0806 13.841V10.3695H10.9193V13.841C9.95179 14.2816 9.27326 15.3097 9.27326 16.5114C9.27326 18.1136 10.4921 19.4221 12.0125 19.4221C13.5329 19.4221 14.7518 18.127 14.7518 16.5114C14.7518 15.3097 14.0733 14.295 13.1057 13.841H13.0806Z"
      fill={fill}
    />
    <path d="M10.9193 9.11422H13.0806V9.86192H10.9193V9.11422Z" fill={fill} />
    <path d="M13.0806 7.81953H10.9193V8.56724H13.0806V7.81953Z" fill={fill} />
    <path d="M10.9193 6.6575H13.0806V7.40521H10.9193V6.6575Z" fill={fill} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.1413 12.6527V5.73641C15.1413 3.89385 13.734 2.39844 12 2.39844C10.2659 2.39844 8.84602 3.89385 8.84602 5.73641V12.6527C7.80309 13.614 7.19995 15.016 7.19995 16.498C7.19995 19.3153 9.34864 21.5984 12 21.5984C14.6513 21.5984 16.8 19.3153 16.8 16.498C16.8 15.016 16.1968 13.614 15.1539 12.6527H15.1413ZM8.23032 16.498C8.23032 18.71 9.91981 20.4964 11.9874 20.5036C14.0675 20.4963 15.7445 18.6966 15.7445 16.498C15.7445 15.1495 15.1162 13.9211 14.0984 13.1868V5.73641C14.0984 4.50803 13.1434 3.49329 11.9874 3.49329C10.8314 3.49329 9.87639 4.49468 9.87639 5.73641V13.1868C8.85859 13.9211 8.23032 15.1495 8.23032 16.498Z"
      fill={fill}
    />
  </svg>
);

TemperatureIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const ChannelIcon = ({ className, fill = '#005D89' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19 5H5L5 19H19V5ZM5 3C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5Z"
      fill={fill}
    />
    <path
      d="M17.0183 12.566L12.996 14.8804L8.97366 17.1949C8.43097 17.5141 8 17.2587 8 16.6362V7.36252C8 6.74002 8.43097 6.48464 8.97366 6.80387L12.996 9.1183L17.0183 11.4327C17.561 11.752 17.561 12.2468 17.0183 12.566Z"
      fill={fill}
    />
  </svg>
);

ChannelIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const OccupancyIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M15.6962 7.63912C15.4228 7.47156 15.119 7.32495 14.8051 7.19929C15.4228 6.48718 15.7671 5.60751 15.7671 4.64407C15.7671 3.60733 15.3924 2.66483 14.6734 1.93178C13.9545 1.19872 13.0532 0.800781 12.0507 0.800781C11.0481 0.800781 10.1367 1.18825 9.42787 1.9213C8.71901 2.66483 8.3342 3.59685 8.3342 4.6336C8.3342 5.59704 8.66838 6.45576 9.27597 7.16787C8.9418 7.29354 8.61775 7.44015 8.32407 7.62865C7.08863 8.40359 6.40002 9.59742 6.40002 10.9902V11.4719C6.40002 11.5452 6.40002 11.6185 6.41015 11.6919C6.41015 11.8175 6.4304 12.2469 6.44053 13.6397C6.44053 14.7288 6.92661 15.7341 7.73673 16.3834V20.3314C7.73673 21.9127 8.9823 23.2008 10.5114 23.2008H13.4988C15.0279 23.2008 16.2734 21.9127 16.2734 20.3314V16.3834C17.1038 15.7236 17.6 14.6974 17.6 13.5873V10.9797C17.6 9.58694 16.9215 8.39311 15.676 7.61817L15.6962 7.63912ZM10.7139 3.27222C11.0886 2.88475 11.5342 2.69625 12.0507 2.69625C12.5671 2.69625 13.0127 2.88475 13.3874 3.27222C13.762 3.65969 13.9443 4.12046 13.9443 4.65454C13.9443 5.18863 13.762 5.6494 13.3874 6.03687C13.0127 6.42434 12.5671 6.61284 12.0507 6.61284C11.5342 6.61284 11.0886 6.42434 10.7139 6.03687C10.3393 5.6494 10.157 5.18863 10.157 4.65454C10.157 4.12046 10.3393 3.65969 10.7139 3.27222ZM15.7975 13.6083C15.7975 14.2575 15.4329 14.823 14.9064 15.0848C14.6431 15.221 14.4608 15.4723 14.4608 15.776V20.3523C14.4608 20.8969 14.0355 21.3367 13.5089 21.3367H10.5215C9.99496 21.3367 9.56964 20.8969 9.56964 20.3523V15.776C9.56964 15.4933 9.40762 15.2314 9.15445 15.1058C8.638 14.8335 8.27344 14.289 8.27344 13.6397C8.26332 11.9013 8.24306 11.4929 8.23294 11.4824V11.0007C8.23294 10.2676 8.57724 9.68119 9.27597 9.25183C9.96458 8.82247 10.8861 8.60256 12.0203 8.60256C13.1545 8.60256 14.0658 8.82247 14.7646 9.25183C15.4532 9.68119 15.8076 10.2676 15.8076 11.0007V13.6083H15.7975Z"
      fill={fill}
    />
  </svg>
);

OccupancyIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const SmartPlugIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M12 12.4998C12.5522 12.5 13 12.9476 13 13.4998V14.4998H11V13.4998C11 12.9476 11.4478 12.5 12 12.4998Z"
      fill={fill}
    />
    <path d="M11 11.4998H10V9.49984H11V11.4998Z" fill={fill} />
    <path d="M14 11.4998H13V9.49984H14V11.4998Z" fill={fill} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.4727 7.9725C17.8874 10.3878 17.667 14.3661 15 16.4998H9C6.41625 14.4328 6.12852 10.6342 8.30859 8.20296L8.52734 7.9725L9 7.49984H15L15.4727 7.9725ZM9.23438 8.67953C7.32078 10.5937 7.41272 13.6964 9.37109 15.4998H14.6289C16.5872 13.6963 16.6793 10.5937 14.7656 8.67953L14.5859 8.49984H9.41406L9.23438 8.67953Z"
      fill={fill}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 3.99984C19.1046 3.99984 20 4.89527 20 5.99984V17.9998C19.9999 19.1043 19.1045 19.9998 18 19.9998H6C4.89549 19.9998 4.0001 19.1043 4 17.9998V5.99984C4 4.89527 4.89543 3.99984 6 3.99984H18ZM6 5.49984C5.72386 5.49984 5.5 5.7237 5.5 5.99984V17.9998C5.5001 18.2759 5.72392 18.4998 6 18.4998H18C18.2761 18.4998 18.4999 18.2759 18.5 17.9998V5.99984C18.5 5.7237 18.2761 5.49984 18 5.49984H6Z"
      fill={fill}
    />
    <path
      d="M19.0039 2.84261C19.0317 2.64741 19.2925 2.53017 19.5537 2.62484C20.3933 2.9317 21.0604 3.60171 21.3662 4.44417C21.4606 4.70631 21.344 4.96805 21.1494 4.99593C20.9549 5.02365 20.7715 4.88447 20.6992 4.68929C20.4657 4.0422 19.9544 3.52914 19.3096 3.29476C19.115 3.22224 18.9761 3.03787 19.0039 2.84261Z"
      fill={fill}
    />
    <path
      d="M19.7314 1.01839C21.2937 1.47028 22.5225 2.703 22.9785 4.27621C23.0508 4.51611 22.929 4.75082 22.7344 4.78988C22.5399 4.8289 22.3513 4.68365 22.29 4.47738C21.9008 3.13838 20.8608 2.0945 19.5264 1.70394C19.3262 1.64815 19.1815 1.4585 19.2148 1.25765L19.2207 1.26351C19.2541 1.07399 19.487 0.95168 19.7314 1.01839Z"
      fill={fill}
    />
  </svg>
);

SmartPlugIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const ToiletFlushIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M7.81445 6.51839C8.07516 6.51839 8.30923 6.68861 8.4082 6.9305H9.58594C9.64889 6.9305 9.70312 6.98495 9.70312 7.04769V7.26253C9.70304 7.32522 9.64884 7.37875 9.58594 7.37875L8.4082 7.38753C8.31827 7.62955 8.08424 7.79085 7.81445 7.79085C7.46396 7.79069 7.17594 7.50447 7.17578 7.15511C7.17578 6.80563 7.46387 6.51855 7.81445 6.51839Z"
      fill={fill}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.77539 3.99984C10.8544 3.99994 11.7719 4.79767 11.9248 5.85531L12.3828 9.33285H18.1113C19.1275 9.33285 19.9551 10.158 19.9551 11.1619C19.955 11.4397 19.8743 11.6999 19.7305 11.9149C19.9012 12.2374 20 12.6047 20 12.9989C20 14.8991 18.7049 16.5398 16.8613 16.9969L15.3867 17.3553L15.1348 18.2694C14.856 19.2823 13.9205 19.9998 12.8594 19.9998H9.35254C8.09355 19.9998 7.06836 18.9774 7.06836 17.7225V16.1366L6.60059 15.8944C5.61155 15.3835 5.00015 14.3707 5 13.2684V6.5096C5 5.12935 6.12388 4.00006 7.51758 3.99984H9.77539ZM7.38184 5.18929C6.70885 5.25682 6.1875 5.82041 6.1875 6.5096V13.2586C6.1875 13.9219 6.55589 14.5318 7.14941 14.8455L7.76953 15.1678C8.06629 15.3292 8.26465 15.6436 8.26465 15.9842V17.7323C8.26487 18.3326 8.75903 18.8249 9.36133 18.825H12.8691C13.3905 18.8248 13.8583 18.4755 13.9932 17.9647L14.3799 16.5575C14.4069 16.45 14.4967 16.3607 14.6045 16.3338L16.5918 15.8494C17.9047 15.5267 18.8311 14.3522 18.8311 13.0077C18.8308 12.3894 18.3273 11.8876 17.707 11.8875H11.8438C11.6639 11.8875 11.5202 11.7531 11.4932 11.5828L11.4658 11.3856H18.543C18.6687 11.3856 18.7674 11.2872 18.7676 11.1619C18.7676 10.8034 18.4708 10.5164 18.1201 10.5164H11.9062C11.5826 10.5163 11.3035 10.2736 11.2676 9.95101L10.7559 6.01644C10.6839 5.53241 10.2788 5.17367 9.78418 5.17367H7.52734L7.38184 5.18929Z"
      fill={fill}
    />
    <path
      d="M19.0039 2.84261C19.0317 2.64741 19.2925 2.53017 19.5537 2.62484C20.3933 2.9317 21.0604 3.60171 21.3662 4.44417C21.4606 4.70631 21.344 4.96804 21.1494 4.99593C20.9549 5.02365 20.7715 4.88447 20.6992 4.68929C20.4657 4.0422 19.9544 3.52914 19.3096 3.29476C19.115 3.22224 18.9761 3.03787 19.0039 2.84261Z"
      fill={fill}
    />
    <path
      d="M19.7314 1.01839C21.2937 1.47028 22.5225 2.703 22.9785 4.27621C23.0508 4.51611 22.929 4.75082 22.7344 4.78988C22.5399 4.8289 22.3513 4.68365 22.29 4.47738C21.9008 3.13838 20.8608 2.0945 19.5264 1.70394C19.3262 1.64815 19.1815 1.4585 19.2148 1.25765L19.2207 1.26351C19.2541 1.07399 19.487 0.95168 19.7314 1.01839Z"
      fill={fill}
    />
  </svg>
);

ToiletFlushIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const DoorOpenIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="62"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 8.00049C20.2091 8.00049 22 9.79135 22 12.0005V36.0005C21.9998 38.2095 20.209 40.0005 18 40.0005H16C13.791 40.0005 12.0002 38.2095 12 36.0005V12.0005C12 9.79135 13.7909 8.00049 16 8.00049H18ZM16 11.0005C15.4477 11.0005 15 11.4482 15 12.0005V36.0005C15.0002 36.5526 15.4478 37.0005 16 37.0005H18C18.5522 37.0005 18.9998 36.5526 19 36.0005V12.0005C19 11.4482 18.5523 11.0005 18 11.0005H16Z"
      fill={fill}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32 8.00049C34.2091 8.00049 36 9.79135 36 12.0005V36.0005C35.9998 38.2095 34.209 40.0005 32 40.0005H30C27.791 40.0005 26.0002 38.2095 26 36.0005V12.0005C26 9.79135 27.7909 8.00049 30 8.00049H32ZM30 11.0005C29.4477 11.0005 29 11.4482 29 12.0005V36.0005C29.0002 36.5526 29.4478 37.0005 30 37.0005H32C32.5522 37.0005 32.9998 36.5526 33 36.0005V24.0005V12.0005C33 11.4482 32.5523 11.0005 32 11.0005H30Z"
      fill={fill}
    />
    <path
      d="M41.71 20.3192C42.0853 20.7549 42.0739 21.4271 41.6872 21.8504L39.8218 23.892L44.9991 23.892C45.5564 23.892 46 24.3775 46 24.9876C46 25.5976 45.5564 26.0831 44.9991 26.0831L39.8218 26.0831L41.6872 28.1247C42.0739 28.548 42.0739 29.2202 41.71 29.6559C41.3232 30.1041 40.6749 30.1166 40.2768 29.6808L36 25L40.2768 20.3192C40.6749 19.8834 41.3232 19.8959 41.71 20.3441L41.71 20.3192Z"
      fill={fill}
    />
    <path
      d="M6.29005 20.3192C5.91469 20.7549 5.92607 21.4271 6.3128 21.8504L8.1782 23.892L3.00095 23.892C2.4436 23.892 2 24.3775 2 24.9876C2 25.5976 2.4436 26.0831 3.00095 26.0831L8.1782 26.0831L6.3128 28.1247C5.92607 28.548 5.92607 29.2202 6.29005 29.6559C6.67678 30.1041 7.32512 30.1166 7.72322 29.6808L12 25L7.72322 20.3192C7.32512 19.8834 6.67678 19.8959 6.29005 20.3441L6.29005 20.3192Z"
      fill={fill}
    />
  </svg>
);

DoorOpenIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const DoorClosedIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="62"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 8.00049C20.2091 8.00049 22 9.79135 22 12.0005V36.0005C21.9998 38.2095 20.209 40.0005 18 40.0005H16C13.791 40.0005 12.0002 38.2095 12 36.0005V12.0005C12 9.79135 13.7909 8.00049 16 8.00049H18ZM16 11.0005C15.4477 11.0005 15 11.4482 15 12.0005V36.0005C15.0002 36.5526 15.4478 37.0005 16 37.0005H18C18.5522 37.0005 18.9998 36.5526 19 36.0005V12.0005C19 11.4482 18.5523 11.0005 18 11.0005H16Z"
      fill={fill}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32 8.00049C34.2091 8.00049 36 9.79135 36 12.0005V36.0005C35.9998 38.2095 34.209 40.0005 32 40.0005H30C27.791 40.0005 26.0002 38.2095 26 36.0005V12.0005C26 9.79135 27.7909 8.00049 30 8.00049H32ZM30 11.0005C29.4477 11.0005 29 11.4482 29 12.0005V36.0005C29.0002 36.5526 29.4478 37.0005 30 37.0005H32C32.5522 37.0005 32.9998 36.5526 33 36.0005V24.0005V12.0005C33 11.4482 32.5523 11.0005 32 11.0005H30Z"
      fill={fill}
    />
    <path
      d="M41.71 20.3192C42.0853 20.7549 42.0739 21.4271 41.6872 21.8504L39.8218 23.892L44.9991 23.892C45.5564 23.892 46 24.3775 46 24.9876C46 25.5976 45.5564 26.0831 44.9991 26.0831L39.8218 26.0831L41.6872 28.1247C42.0739 28.548 42.0739 29.2202 41.71 29.6559C41.3232 30.1041 40.6749 30.1166 40.2768 29.6808L36 25L40.2768 20.3192C40.6749 19.8834 41.3232 19.8959 41.71 20.3441L41.71 20.3192Z"
      fill={fill}
    />
    <path
      d="M6.29005 20.3192C5.91469 20.7549 5.92607 21.4271 6.3128 21.8504L8.1782 23.892L3.00095 23.892C2.4436 23.892 2 24.3775 2 24.9876C2 25.5976 2.4436 26.0831 3.00095 26.0831L8.1782 26.0831L6.3128 28.1247C5.92607 28.548 5.92607 29.2202 6.29005 29.6559C6.67678 30.1041 7.32512 30.1166 7.72322 29.6808L12 25L7.72322 20.3192C7.32512 19.8834 6.67678 19.8959 6.29005 20.3441L6.29005 20.3192Z"
      fill={fill}
    />
  </svg>
);

DoorClosedIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const OccupiedIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M9.03223 17.9385C7.14013 17.9385 5.53893 18.3045 4.30762 19.0713C3.02823 19.8576 2.34574 20.9828 2.3457 22.3926V23.4092L2.36328 23.4277C2.36756 23.5285 2.37416 23.6727 2.37891 23.873C2.38495 24.128 2.39091 24.4703 2.39648 24.9229L2.41113 26.6377C2.41254 27.9128 3.13068 28.9847 4.14648 29.5205L4.15332 29.5244L4.16016 29.5273C4.36245 29.6278 4.49414 29.8404 4.49414 30.0674V37.4219C4.49414 38.6093 5.42754 39.6045 6.62402 39.6045H11.4248C12.6213 39.6045 13.5547 38.6093 13.5547 37.4219V30.0674C13.5547 29.843 13.6849 29.6284 13.9385 29.4941C14.8215 29.055 15.4628 28.1967 15.6475 27.1836H15.7197V22.3926C15.7197 20.9786 15.0169 19.8566 13.7607 19.0732L13.7568 19.0713L13.5195 18.9316C12.3128 18.261 10.8097 17.9385 9.03223 17.9385ZM9.08105 8.44629C8.08508 8.44637 7.21457 8.81784 6.50195 9.55469C5.78992 10.291 5.43754 11.1819 5.4375 12.1934C5.4375 13.2049 5.78988 14.0957 6.50195 14.832C7.2146 15.569 8.085 15.9404 9.08105 15.9404C10.0772 15.9404 10.9484 15.5691 11.6611 14.832C12.3731 14.0957 12.7246 13.2048 12.7246 12.1934C12.7246 11.1819 12.3732 10.291 11.6611 9.55469C10.9484 8.81765 10.0772 8.44629 9.08105 8.44629ZM0.616211 23.4619L0.610352 23.4219C0.600658 23.3517 0.599611 23.2747 0.599609 23.1504V22.376C0.599609 20.358 1.58843 18.6247 3.41113 17.4814L3.41602 17.4785C3.83969 17.2066 4.31822 16.9875 4.83301 16.7939L5.63281 16.4932L5.07812 15.8438C4.19072 14.8037 3.70905 13.5602 3.70898 12.1602C3.70898 10.6534 4.26395 9.30356 5.29785 8.21777C6.32527 7.15541 7.63462 6.59969 9.08105 6.59961C10.521 6.59961 11.8182 7.16779 12.8682 8.23828C13.91 9.30063 14.4541 10.6627 14.4541 12.1768C14.4541 13.5755 13.9563 14.8513 13.0547 15.8906L12.5029 16.5273L13.2852 16.8408C13.7653 17.033 14.2223 17.254 14.627 17.502L14.6328 17.4932C16.4406 18.6351 17.4004 20.3547 17.4004 22.3594V26.5498C17.4004 28.1548 16.6828 29.6293 15.4951 30.5732L15.2676 30.7539V37.3887C15.2675 39.6177 13.5155 41.4004 11.4082 41.4004H6.60742C4.50024 41.4003 2.74814 39.6177 2.74805 37.3887V30.7559L2.52344 30.5752C1.36622 29.6477 0.665039 28.2054 0.665039 26.6338V26.6299C0.656883 25.5081 0.645003 24.7726 0.634766 24.292C0.62426 23.7988 0.616233 23.5973 0.616211 23.5039V23.4619Z"
      fill={fill}
      stroke={fill}
      strokeWidth="1.2"
    />
    <path d="M18.2002 10L22.5 14M28.2002 7L21.2002 14" stroke={fill} strokeWidth="2" />
  </svg>
);

OccupiedIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const VacantIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="46"
    height="41"
    viewBox="0 0 46 41"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M9.03223 11.9385C7.14013 11.9385 5.53893 12.3045 4.30762 13.0713C3.02823 13.8576 2.34574 14.9828 2.3457 16.3926V17.4092L2.36328 17.4277C2.36756 17.5285 2.37416 17.6727 2.37891 17.873C2.38495 18.128 2.39091 18.4703 2.39648 18.9229L2.41113 20.6377C2.41254 21.9128 3.13068 22.9847 4.14648 23.5205L4.15332 23.5244L4.16016 23.5273C4.36245 23.6278 4.49414 23.8404 4.49414 24.0674V31.4219C4.49414 32.6093 5.42754 33.6045 6.62402 33.6045H11.4248C12.6213 33.6045 13.5547 32.6093 13.5547 31.4219V24.0674C13.5547 23.843 13.6849 23.6284 13.9385 23.4941C14.8215 23.055 15.4628 22.1967 15.6475 21.1836H15.7197V16.3926C15.7197 14.9786 15.0169 13.8566 13.7607 13.0732L13.7568 13.0713L13.5195 12.9316C12.3128 12.261 10.8097 11.9385 9.03223 11.9385ZM9.08105 2.44629C8.08508 2.44637 7.21457 2.81784 6.50195 3.55469C5.78992 4.29102 5.43754 5.18193 5.4375 6.19336C5.4375 7.20486 5.78988 8.09565 6.50195 8.83203C7.2146 9.569 8.085 9.94035 9.08105 9.94043C10.0772 9.94043 10.9484 9.56908 11.6611 8.83203C12.3731 8.09568 12.7246 7.20478 12.7246 6.19336C12.7246 5.18193 12.3732 4.29102 11.6611 3.55469C10.9484 2.81765 10.0772 2.44629 9.08105 2.44629ZM0.616211 17.4619L0.610352 17.4219C0.600658 17.3517 0.599611 17.2747 0.599609 17.1504V16.376C0.599609 14.358 1.58843 12.6247 3.41113 11.4814L3.41602 11.4785C3.83969 11.2066 4.31822 10.9875 4.83301 10.7939L5.63281 10.4932L5.07812 9.84375C4.19072 8.8037 3.70905 7.56017 3.70898 6.16016C3.70898 4.65341 4.26395 3.30356 5.29785 2.21777C6.32527 1.15541 7.63462 0.599691 9.08105 0.599609C10.521 0.599609 11.8182 1.16779 12.8682 2.23828C13.91 3.30063 14.4541 4.6627 14.4541 6.17676C14.4541 7.57547 13.9563 8.85129 13.0547 9.89062L12.5029 10.5273L13.2852 10.8408C13.7653 11.033 14.2223 11.254 14.627 11.502L14.6328 11.4932C16.4406 12.6351 17.4004 14.3547 17.4004 16.3594V20.5498C17.4004 22.1548 16.6828 23.6293 15.4951 24.5732L15.2676 24.7539V31.3887C15.2675 33.6177 13.5155 35.4004 11.4082 35.4004H6.60742C4.50024 35.4003 2.74814 33.6177 2.74805 31.3887V24.7559L2.52344 24.5752C1.36622 23.6477 0.665039 22.2054 0.665039 20.6338V20.6299C0.656883 19.5081 0.645003 18.7726 0.634766 18.292C0.62426 17.7988 0.616233 17.5973 0.616211 17.5039V17.4619Z"
      fill={fill}
      stroke={fill}
      strokeWidth="1.2"
    />
    <path d="M19.2002 1L26.2002 8M26.2002 1L19.2002 8" stroke={fill} strokeWidth="2" />
  </svg>
);

VacantIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const SmartPlugPowerOnIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="30"
    viewBox="0 0 24 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M18.9583 1.40625C18.9583 0.644531 18.2899 0 17.5 0C16.6493 0 16.0417 0.644531 16.0417 1.40625V6.5625H18.9583V1.40625ZM7.29167 1.40625C7.29167 0.644531 6.62326 0 5.83333 0C4.98264 0 4.375 0.644531 4.375 1.40625V6.5625H7.29167V1.40625ZM22.3611 8.4375H0.972222C0.425347 8.4375 0 8.90625 0 9.375V10.3125C0 10.8398 0.425347 11.25 0.972222 11.25H1.94444V15C1.94444 19.7461 5.52951 23.5547 10.2083 24.2578V29C10.2083 29.5523 10.656 30 11.2083 30H12.125C12.6773 30 13.125 29.5523 13.125 29V24.2578C17.7431 23.5547 21.3889 19.7461 21.3889 15V11.25H22.3611C22.8472 11.25 23.3333 10.8398 23.3333 10.3125V9.375C23.3333 8.90625 22.8472 8.4375 22.3611 8.4375ZM18.4722 15C18.4722 18.6328 15.3733 21.5625 11.6667 21.5625C7.89931 21.5625 4.86111 18.6328 4.86111 15V11.25H18.4722V15Z"
      fill={fill}
    />
  </svg>
);

SmartPlugPowerOnIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const SmartPlugPowerOffIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="32"
    height="37"
    viewBox="0 0 32 37"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M22.6943 10.4375C23.1804 10.4375 23.667 10.9062 23.667 11.375V12.3125C23.667 12.8398 23.1804 13.25 22.6943 13.25H21.7227V17C21.7227 21.7461 18.0761 25.5547 13.458 26.2578V31C13.458 31.5523 13.0103 32 12.458 32H11.542C10.9897 32 10.542 31.5523 10.542 31V26.2578C9.51126 26.1029 8.53368 25.7964 7.63574 25.3633L9.79004 23.209C10.4827 23.4372 11.2257 23.5625 12 23.5625C15.7066 23.5625 18.8057 20.6328 18.8057 17V14.1943L22.5625 10.4375H22.6943ZM16.1982 10.4375L13.3857 13.25H5.19434V17C5.19434 18.2617 5.56207 19.4377 6.19824 20.4365L4.12695 22.5078C2.9638 20.9721 2.27734 19.0758 2.27734 17V13.25H1.30566C0.758789 13.25 0.333008 12.8398 0.333008 12.3125V11.375C0.333008 10.9062 0.758789 10.4375 1.30566 10.4375H16.1982ZM6.16699 2C6.95677 2.00019 7.625 2.64464 7.625 3.40625V8.5625H4.70801V3.40625C4.70801 2.64453 5.3163 2 6.16699 2ZM17.833 2C18.6229 2 19.292 2.64453 19.292 3.40625V7.34375L18.0732 8.5625H16.375V3.40625C16.375 2.64462 16.9825 2.00015 17.833 2Z"
      fill={fill}
    />
    <line
      x1="4"
      y1="26.2322"
      x2="23.2322"
      y2="7"
      stroke={fill}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

SmartPlugPowerOffIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const SmartPlugPowerActiveIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="24"
    height="35"
    viewBox="0 0 28 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path
      d="M27.5081 11.8125C26.9252 10.8281 25.8322 10.1953 24.6663 10.1953H17.7438L19.857 4.07812C20.1484 3.09375 19.9298 2.10938 19.274 1.26562C18.6182 0.492188 17.6709 0 16.6508 0H7.10501C5.42904 0 4.04454 1.26562 3.82593 2.67188L0.0367787 17.5781C-0.108958 18.5625 0.182515 19.4766 0.83833 20.25C1.49415 20.9531 2.5143 21.375 3.53446 21.375H10.6756L8.12516 32.0625C7.90656 33.0469 8.12516 34.0312 8.70811 34.8047C9.36392 35.5781 10.3112 36 11.3314 36C12.5701 36 13.6632 35.4375 14.1732 34.5234L27.5081 15.0469C28.164 14.0625 28.164 12.8672 27.5081 11.8125Z"
      fill={fill}
    />
  </svg>
);

SmartPlugPowerActiveIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };

export const SmartPlugPowerInactiveIcon = ({ className, fill = '#005D98' }) => (
  <svg
    width="42"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <g clipPath="url(#clip0_838_16363)">
      <path
        d="M37.5081 17.8125C36.9252 16.8281 35.8322 16.1953 34.6663 16.1953H27.7438L29.857 10.0781C30.1484 9.09375 29.9298 8.10938 29.274 7.26562C28.6182 6.49219 27.6709 6 26.6508 6H17.105C15.429 6 14.0445 7.26562 13.8259 8.67188L10.0368 23.5781C9.89104 24.5625 10.1825 25.4766 10.8383 26.25C11.4941 26.9531 12.5143 27.375 13.5345 27.375H20.6756L18.1252 38.0625C17.9066 39.0469 18.1252 40.0312 18.7081 40.8047C19.3639 41.5781 20.3112 42 21.3314 42C22.5701 42 23.6632 41.4375 24.1732 40.5234L37.5081 21.0469C38.164 20.0625 38.164 18.8672 37.5081 17.8125ZM21.6957 38.2031L25.0477 24H13.4616L17.105 9.375H26.4322L22.9345 19.5703H34.3748L21.6957 38.2031Z"
        fill={fill}
      />
    </g>
    <defs>
      <clipPath id="clip0_838_16363">
        <rect width="48" height="48" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

SmartPlugPowerInactiveIcon.propTypes = { className: PropTypes.string, fill: PropTypes.string };
